<template>
  <div class="privacy-policy-container bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
    <div class="max-w-4xl mx-auto px-6 py-12 md:px-8 lg:py-16">

      <!-- Header -->
      <header class="mb-12">
        <h1 class="text-4xl font-bold mb-3 tracking-tight">Privacy Policy</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">Last updated April 26, 2026</p>
      </header>

      <!-- Introduction -->
      <section class="prose-section">
        <p>
          This Privacy Notice for BetterConnect ("we", "us", or "our"), describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:
        </p>
        <ul>
          <li>Visit our website at <a href="http://www.betterconnect.app" class="link">betterconnect.app</a>, or any website of ours that links to this Privacy Notice</li>
          <li>Download and use our mobile application (BetterConnect), or any other application of ours that links to this Privacy Notice</li>
          <li>Engage with us in other related ways, including any marketing or events</li>
        </ul>
        <p>
          <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible for making decisions about how your personal information is processed. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:dpo@betterconnect.app" class="link">dpo@betterconnect.app</a>.
        </p>
      </section>

      <!-- Summary of Key Points -->
      <section class="prose-section">
        <h2>Summary of Key Points</h2>
        <p class="italic text-gray-600 dark:text-gray-400 mb-6">
          This summary provides key points from our Privacy Notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for.
        </p>

        <div class="space-y-4">
          <div>
            <strong>What personal information do we process?</strong>
            <span class="text-gray-600 dark:text-gray-400"> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use. Learn more about </span>
            <a href="#section1" class="link">personal information you disclose to us</a>.
          </div>

          <div>
            <strong>Do we process any sensitive personal information?</strong>
            <span class="text-gray-600 dark:text-gray-400"> Some of the information may be considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We may process sensitive personal information when necessary with your consent or as otherwise permitted by applicable law. Learn more about </span>
            <a href="#section1" class="link">sensitive information we process</a>.
          </div>

          <div>
            <strong>Do we collect any information from third parties?</strong>
            <span class="text-gray-600 dark:text-gray-400"> We may collect information from your educational institution's student information system in order to provide our Services. Learn more about </span>
            <a href="#othersources" class="link">information collected from other sources</a>.
          </div>

          <div>
            <strong>How do we process your information?</strong>
            <span class="text-gray-600 dark:text-gray-400"> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Learn more about </span>
            <a href="#section2" class="link">how we process your information</a>.
          </div>

          <div>
            <strong>In what situations and with which parties do we share personal information?</strong>
            <span class="text-gray-600 dark:text-gray-400"> We may share information in specific situations and with specific third parties. Learn more about </span>
            <a href="#section3" class="link">when and with whom we share your personal information</a>.
          </div>

          <div>
            <strong>How do we keep your information safe?</strong>
            <span class="text-gray-600 dark:text-gray-400"> We have adequate organisational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Learn more about </span>
            <a href="#section5" class="link">how we keep your information safe</a>.
          </div>

          <div>
            <strong>What are your rights?</strong>
            <span class="text-gray-600 dark:text-gray-400"> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information. Learn more about </span>
            <a href="#section6" class="link">your privacy rights</a>.
          </div>

          <div>
            <strong>How do you exercise your rights?</strong>
            <span class="text-gray-600 dark:text-gray-400"> The easiest way to exercise your rights is by visiting </span>
            <a href="http://www.betterconnect.app/data" class="link">betterconnect.app/data</a>
            <span class="text-gray-600 dark:text-gray-400">, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.</span>
          </div>
        </div>
      </section>

      <!-- Table of Contents -->
      <section class="prose-section">
        <h2>Table of Contents</h2>
        <nav class="toc">
          <ol>
            <li><a href="#section1">What Information Do We Collect?</a></li>
            <li><a href="#section2">How Do We Process Your Information?</a></li>
            <li><a href="#section3">When and With Whom Do We Share Your Personal Information?</a></li>
            <li><a href="#section4">How Long Do We Keep Your Information?</a></li>
            <li><a href="#section5">How Do We Keep Your Information Safe?</a></li>
            <li><a href="#section6">What Are Your Privacy Rights?</a></li>
            <li><a href="#section7">Controls for Do-Not-Track Features</a></li>
            <li><a href="#section8">Do United States Residents Have Specific Privacy Rights?</a></li>
            <li><a href="#section9">Do We Make Updates to This Notice?</a></li>
            <li><a href="#section10">How Can You Contact Us About This Notice?</a></li>
            <li><a href="#section11">How Can You Review, Update, or Delete the Data We Collect From You?</a></li>
          </ol>
        </nav>
      </section>

      <!-- Section 1 -->
      <section id="section1" class="prose-section">
        <h2>1. What Information Do We Collect?</h2>

        <h3 id="personalinfo">Personal information you disclose to us</h3>
        <p><strong>In Short:</strong> <em>We collect personal information that you provide to us.</em></p>

        <p>
          We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
        </p>

        <h4>Sensitive Information</h4>
        <p>When necessary, with your consent or as otherwise permitted by applicable law, we process the following categories of sensitive information:</p>
        <ul>
          <li>Student data</li>
        </ul>

        <h4>Application Data</h4>
        <p>If you use our application(s), we also may collect the following information if you choose to provide us with access or permission:</p>
        <ul>
          <li><em>Mobile Device Access.</em> We may request access or permission to certain features from your mobile device. If you wish to change our access or permissions, you may do so in your device's settings.</li>
          <li><em>Push Notifications.</em> We may request to send you push notifications regarding your account or certain features of the application(s). If you wish to opt out from receiving these types of communications, you may turn them off in your device's settings.</li>
          <li><em>Mobile Phone Number.</em> We may collect your mobile phone number if you choose to opt in to receive SMS text message notifications from us. We use your mobile phone number solely to send you daily digest messages summarizing your first-period class information (up to one message per school day). Message and data rates may apply. Your mobile phone number and SMS opt-in data will not be sold, rented, or shared with third parties or affiliates for their own marketing purposes.</li>
        </ul>
        <p>This information is primarily needed to maintain the security and operation of our application(s), for troubleshooting, and for our internal analytics and reporting purposes.</p>

        <p>All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.</p>

        <h3 id="othersources">Information collected from other sources</h3>
        <p><strong>In Short:</strong> <em>We may collect limited data from your educational institution's student information system.</em></p>

        <p>
          In order to provide our Services, we may obtain information about you from your educational institution's student information system, Blackbaud. This information may include your name, profile picture, class enrolment, schedule and assignments. This data is collected solely for the purpose of delivering educational services to you through the BetterConnect platform and is not used for advertising, marketing, or any commercial purpose unrelated to providing the Services.
        </p>
      </section>

      <!-- Section 2 -->
      <section id="section2" class="prose-section">
        <h2>2. How Do We Process Your Information?</h2>
        <p><strong>In Short:</strong> <em>We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</em></p>

        <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>

        <ul>
          <li><strong>To facilitate account creation and authentication and otherwise manage user accounts.</strong> We may process your information so you can create and log in to your account, as well as keep your account in working order.</li>
          <li><strong>To deliver and facilitate delivery of services to the user.</strong> We may process your information to provide you with the requested service.</li>
          <li><strong>To respond to user inquiries/offer support to users.</strong> We may process your information to respond to your inquiries and solve any potential issues you might have with the requested service.</li>
          <li><strong>To evaluate and improve our Services, products, marketing, and your experience.</strong> We may process your information when we believe it is necessary to identify usage trends, determine the effectiveness of our promotional campaigns, and to evaluate and improve our Services, products, marketing, and your experience.</li>
          <li><strong>To comply with our legal obligations.</strong> We may process your information to comply with our legal obligations, respond to legal requests, and exercise, establish, or defend our legal rights.</li>
        </ul>
      </section>

      <!-- Section 3 -->
      <section id="section3" class="prose-section">
        <h2>3. When and With Whom Do We Share Your Personal Information?</h2>
        <p><strong>In Short:</strong> <em>We may share information in specific situations described in this section and/or with the following third parties.</em></p>

        <p>We may need to share your personal information in the following situations:</p>

        <ul>
          <li><strong>Educational Institutions.</strong> We may share your information with the educational institution with which you are affiliated in order to support the delivery of educational services and to comply with school policies or legal obligations.</li>
        </ul>
      </section>

      <!-- Section 4 -->
      <section id="section4" class="prose-section">
        <h2>4. How Long Do We Keep Your Information?</h2>
        <p><strong>In Short:</strong> <em>We keep your information for as long as necessary to fulfil the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>

        <p>
          We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.
        </p>

        <p>
          When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymise such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
        </p>
      </section>

      <!-- Section 5 -->
      <section id="section5" class="prose-section">
        <h2>5. How Do We Keep Your Information Safe?</h2>
        <p><strong>In Short:</strong> <em>We aim to protect your personal information through a system of organisational and technical security measures.</em></p>

        <p>
          We have implemented appropriate and reasonable technical and organisational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
        </p>
      </section>

      <!-- Section 6 -->
      <section id="section6" class="prose-section">
        <h2>6. What Are Your Privacy Rights?</h2>
        <p><strong>In Short:</strong> <em>You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.</em></p>

        <h3>Withdrawing your consent</h3>
        <p>
          If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section "<a href="#section10" class="link">How Can You Contact Us About This Notice?</a>" below.
        </p>

        <p>
          However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.
        </p>

        <h3>Account Information</h3>
        <p>If you would at any time like to review or change the information in your account or terminate your account, you can:</p>
        <ul>
          <li>Contact us using the contact information provided.</li>
        </ul>

        <p>
          Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
        </p>

        <p>
          If you have questions or comments about your privacy rights, you may email us at <a href="mailto:dpo@betterconnect.app" class="link">dpo@betterconnect.app</a>.
        </p>
      </section>

      <!-- Section 7 -->
      <section id="section7" class="prose-section">
        <h2>7. Controls for Do-Not-Track Features</h2>
        <p>
          Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognising and implementing DNT signals has been finalised. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.
        </p>

        <p>
          California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognising or honouring DNT signals, we do not respond to them at this time.
        </p>
      </section>

      <!-- Section 8 -->
      <section id="section8" class="prose-section">
        <h2>8. Do United States Residents Have Specific Privacy Rights?</h2>
        <p><strong>In Short:</strong> <em>If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. More information is provided below.</em></p>

        <h3>Categories of Personal Information We Collect</h3>
        <p>The table below shows the categories of personal information we have collected in the past twelve (12) months. The table includes illustrative examples of each category and does not reflect the personal information we collect from you. For a comprehensive inventory of all personal information we process, please refer to the section "<a href="#section1" class="link">What Information Do We Collect?</a>"</p>

        <div class="table-container">
          <table>
            <thead>
            <tr>
              <th>Category</th>
              <th>Examples</th>
              <th>Collected</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td>A. Identifiers</td>
              <td>Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>B. Personal information as defined in the California Customer Records statute</td>
              <td>Name, contact information, education, employment, employment history, and financial information</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>C. Protected classification characteristics under state or federal law</td>
              <td>Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>D. Commercial information</td>
              <td>Transaction information, purchase history, financial details, and payment information</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>E. Biometric information</td>
              <td>Fingerprints and voiceprints</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>F. Internet or other similar network activity</td>
              <td>Browsing history, search history, online behaviour, interest data, and interactions with our and other websites, applications, systems, and advertisements</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>G. Geolocation data</td>
              <td>Device location</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>H. Audio, electronic, sensory, or similar information</td>
              <td>Images and audio, video or call recordings created in connection with our business activities</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>I. Professional or employment-related information</td>
              <td>Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>J. Education Information</td>
              <td>Custom Assignments</td>
              <td>YES</td>
            </tr>
            <tr>
              <td>K. Inferences drawn from collected personal information</td>
              <td>Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics</td>
              <td>NO</td>
            </tr>
            <tr>
              <td>L. Sensitive personal Information</td>
              <td>Personal data from a known child</td>
              <td>YES</td>
            </tr>
            </tbody>
          </table>
        </div>

        <p>
          We only collect sensitive personal information, as defined by applicable privacy laws or the purposes allowed by law or with your consent. Sensitive personal information may be used, or disclosed to a service provider or contractor, for additional, specified purposes. You may have the right to limit the use or disclosure of your sensitive personal information. We do not collect or process sensitive personal information for the purpose of inferring characteristics about you.
        </p>

        <p>We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:</p>
        <ul>
          <li>Receiving help through our customer support channels;</li>
          <li>Participation in customer surveys or contests; and</li>
          <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
        </ul>

        <p>We will use and retain the collected personal information as needed to provide the Services or for:</p>
        <ul>
          <li>Category A - As long as the user has an account with us</li>
          <li>Category B - As long as the user has an account with us</li>
          <li>Category J - As long as the user has an account with us</li>
          <li>Category L - As long as the user has an account with us</li>
        </ul>

        <h3>Sources of Personal Information</h3>
        <p>Learn more about the sources of personal information we collect in "<a href="#section1" class="link">What Information Do We Collect?</a>"</p>

        <h3>How We Use and Share Personal Information</h3>
        <p>Learn more about how we use your personal information in the section, "<a href="#section2" class="link">How Do We Process Your Information?</a>"</p>

        <h3>Will your information be shared with anyone else?</h3>
        <p>
          We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information to in the section, "<a href="#section3" class="link">When and With Whom Do We Share Your Personal Information?</a>"
        </p>

        <p>
          We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be "selling" of your personal information.
        </p>

        <p>
          We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months. We will not sell or share personal information in the future belonging to website visitors, users, and other consumers.
        </p>

        <h3>Your Rights</h3>
        <p>You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:</p>
        <ul>
          <li><strong>Right to know</strong> whether or not we are processing your personal data</li>
          <li><strong>Right to access</strong> your personal data</li>
          <li><strong>Right to correct</strong> inaccuracies in your personal data</li>
          <li><strong>Right to request</strong> the deletion of your personal data</li>
          <li><strong>Right to obtain a copy</strong> of the personal data you previously shared with us</li>
          <li><strong>Right to non-discrimination</strong> for exercising your rights</li>
          <li><strong>Right to opt out</strong> of the processing of your personal data if it is used for targeted advertising (or sharing as defined under California's privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects ("profiling")</li>
        </ul>

        <p>Depending upon the state where you live, you may also have the following rights:</p>
        <ul>
          <li>Right to access the categories of personal data being processed (as permitted by applicable law, including the privacy law in Minnesota)</li>
          <li>Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in California, Delaware, and Maryland)</li>
          <li>Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in Minnesota and Oregon)</li>
          <li>Right to obtain a list of third parties to which we have sold personal data (as permitted by applicable law, including the privacy law in Connecticut)</li>
          <li>Right to review, understand, question, and depending on where you live, correct how personal data has been profiled (as permitted by applicable law, including the privacy law in Connecticut and Minnesota)</li>
          <li>Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including the privacy law in California)</li>
          <li>Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including the privacy law in Florida)</li>
        </ul>

        <h3>How to Exercise Your Rights</h3>
        <p>
          To exercise these rights, you can contact us by visiting <a href="http://www.betterconnect.app/data" class="link">betterconnect.app/data</a>, by emailing us at <a href="mailto:dpo@betterconnect.app" class="link">dpo@betterconnect.app</a>, or by referring to the contact details at the bottom of this document.
        </p>

        <p>
          Under certain US state data protection laws, you can designate an authorised agent to make a request on your behalf. We may deny a request from an authorised agent that does not submit proof that they have been validly authorised to act on your behalf in accordance with applicable laws.
        </p>

        <h3>Request Verification</h3>
        <p>
          Upon receiving your request, we will need to verify your identity to determine you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information for the purposes of verifying your identity and for security or fraud-prevention purposes.
        </p>

        <p>
          If you submit the request through an authorised agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf.
        </p>

        <h3>Appeals</h3>
        <p>
          Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at <a href="mailto:dpo@betterconnect.app" class="link">dpo@betterconnect.app</a>. We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general.
        </p>

        <h3>California "Shine The Light" Law</h3>
        <p>
          California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us by using the contact details provided in the section "<a href="#section10" class="link">How Can You Contact Us About This Notice?</a>"
        </p>
      </section>

      <!-- Section 9 -->
      <section id="section9" class="prose-section">
        <h2>9. Do We Make Updates to This Notice?</h2>
        <p><strong>In Short:</strong> <em>Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>

        <p>
          We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.
        </p>
      </section>

      <!-- Section 10 -->
      <section id="section10" class="prose-section">
        <h2>10. How Can You Contact Us About This Notice?</h2>
        <p>
          If you have questions or comments about this notice, you may contact our Data Protection Officer (DPO) by email at <a href="mailto:dpo@betterconnect.app" class="link">dpo@betterconnect.app</a>, by phone at <a href="tel:+19179151957" class="link">1-917-915-1957</a>, or contact us by post at:
        </p>

        <address class="contact-address">
          <strong>BetterConnect</strong><br>
          Data Protection Officer<br>
          1228 Madison Avenue<br>
          Apt 14<br>
          New York, NY 10128<br>
          United States
        </address>
      </section>

      <!-- Section 11 -->
      <section id="section11" class="prose-section">
        <h2>11. How Can You Review, Update, or Delete the Data We Collect From You?</h2>
        <p>
          Based on the applicable laws of your country or state of residence in the US, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. To request to review, update, or delete your personal information, please visit: <a href="http://www.betterconnect.app/data" class="link">betterconnect.app/data</a>.
        </p>
      </section>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
};
</script>

<style scoped>
.privacy-policy-container {
  min-height: 100vh;
}

/* Typography */
h1 {
  @apply text-gray-900 dark:text-white;
}

h2 {
  @apply text-2xl font-bold mt-10 mb-4 text-gray-900 dark:text-white pb-2 border-b border-gray-200 dark:border-gray-700;
}

h3 {
  @apply text-xl font-semibold mt-6 mb-3 text-gray-800 dark:text-gray-100;
}

h4 {
  @apply text-lg font-medium mt-4 mb-2 text-gray-700 dark:text-gray-200;
}

/* Prose sections */
.prose-section {
  @apply mb-8;
}

.prose-section p {
  @apply text-gray-600 dark:text-gray-300 leading-relaxed mb-4;
}

.prose-section ul {
  @apply list-disc ml-6 mb-4 space-y-2 text-gray-600 dark:text-gray-300;
}

.prose-section li {
  @apply leading-relaxed;
}

/* Links */
.link {
  @apply text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-2 transition-colors;
}

/* Table of Contents */
.toc ol {
  @apply list-decimal ml-6 space-y-2;
}

.toc a {
  @apply text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors no-underline;
}

.toc a:hover {
  @apply underline;
}

/* Table */
.table-container {
  @apply overflow-x-auto my-6 rounded-lg border border-gray-200 dark:border-gray-700;
}

table {
  @apply w-full text-sm;
}

thead {
  @apply bg-gray-50 dark:bg-gray-800;
}

th {
  @apply px-4 py-3 text-left font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700;
}

td {
  @apply px-4 py-3 text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800;
}

tbody tr:last-child td {
  @apply border-b-0;
}

tbody tr:hover {
  @apply bg-gray-50 dark:bg-gray-800/50;
}

/* Contact address */
.contact-address {
  @apply mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-600 dark:text-gray-300 not-italic leading-relaxed;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .privacy-policy-container {
    @apply px-4 py-8;
  }

  h1 {
    @apply text-3xl;
  }

  h2 {
    @apply text-xl;
  }

  h3 {
    @apply text-lg;
  }

  table {
    @apply text-xs;
  }

  th, td {
    @apply px-2 py-2;
  }
}

/* Smooth scroll for anchor links */
html {
  scroll-behavior: smooth;
}
</style>